// Section 22 Build tab: what can be queued, with build points (explain() of buildCost),
// materials on hand versus needed, what the building gives, and owned against the limit.
// Props only.
import type { BuildingTypeId, Resource } from '../../sim/api';
import type { BuildOption } from '../../viewModels/build';
import { buildText, resourceLabel } from '../text/en';
import { Explain } from './explain';
import { amount, type Materials } from './materials';

interface Props {
  options: readonly BuildOption[];
  stock: Materials;
  canEdit: boolean;
  onQueue: (type: BuildingTypeId) => void;
}

function gives(o: BuildOption): string {
  const parts: string[] = [];
  if (o.beds > 0) parts.push(buildText.beds(o.beds));
  if (o.plots > 0) parts.push(buildText.plots(o.plots));
  for (const [r, n] of Object.entries(o.capacity) as [Resource, number][])
    parts.push(buildText.capacity(n, resourceLabel[r].toLowerCase()));
  return parts.join(', ');
}

export function BuildOptions({ options, stock, canEdit, onQueue }: Props) {
  return (
    <table class="table build__options">
      <thead>
        <tr>
          <th scope="col">{buildText.colBuilding}</th>
          <th scope="col">{buildText.colCost}</th>
          <th scope="col">{buildText.colMaterials}</th>
          <th scope="col">{buildText.colGives}</th>
          <th scope="col">{buildText.colOwned}</th>
          <th scope="col">
            <span class="visually-hidden">{buildText.colActions}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {options.map((o) => (
          <tr key={o.id}>
            <th scope="row">{o.label}</th>
            <td>
              <Explain
                value={amount(o.cost)}
                factors={o.costFactors}
                label={`${buildText.colCost}: ${o.label}`}
              />
            </td>
            <td>
              <ul class="build__materials">
                {(Object.entries(o.materials) as [Resource, number][]).map(([r, need]) => {
                  const short = o.shortfall[r];
                  return (
                    <li key={r} class={short === undefined ? undefined : 'is-short'}>
                      <span class="num">
                        {amount(stock[r] ?? 0)} / {amount(need)}
                      </span>{' '}
                      {resourceLabel[r].toLowerCase()}
                      {short !== undefined && (
                        <strong class="build__short"> {buildText.short(amount(short))}</strong>
                      )}
                    </li>
                  );
                })}
              </ul>
            </td>
            <td>{gives(o)}</td>
            <td class="num">
              {o.planned} / {o.limit === null ? buildText.noLimit : o.limit}
            </td>
            <td>
              <button
                type="button"
                class="button button--small"
                disabled={!canEdit || o.atLimit}
                onClick={() => onQueue(o.id)}
              >
                {buildText.queueButton}
              </button>
              {o.atLimit && <span class="hint"> {buildText.limitReached}</span>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
